import React, {createContext, useCallback, useContext, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {isClientRole} from "@/app/auth/AuthContext";
import {requestFeedbackApi} from "@/boxhub-api";
import {OpenRequestFeedbackDTO, UserRoleType} from "@/api/data-contracts";
import {useSnackbarEx} from "@/lib/snackbarex";
import PostLoginFeedbackDialog from "@/pages/login-page/feedback/PostLoginFeedbackDialog";
import {FeedbackEntryState} from "@/pages/login-page/feedback/feedback-dialog-logic";
import {PostLoginFeedbackDialogI18N} from "@/translations";

interface FeedbackDialogContextType {
    /**
     * Fire-and-forget: fetches all open (PENDING) feedback markers of the given company roles and
     * opens the blocking stacked dialog when at least one exists. Must never throw or block the
     * caller — every failure path is swallowed internally.
     *
     * `roles` is passed explicitly by the caller (rather than read via useAuth() inside this
     * provider) because the intended call site — UserAuthForm.handleSubmit, immediately after
     * `await login()` — runs before React re-renders AuthProvider with the just-created session.
     * Reading roles via useAuth() at that point would close over the STALE pre-login value
     * (undefined on a fresh interactive login), silently suppressing the dialog forever.
     */
    triggerFeedbackCheck: (roles: UserRoleType[] | undefined) => Promise<void>;
}

const FeedbackDialogContext = createContext<FeedbackDialogContextType | undefined>(undefined);

/**
 * Global host + context for the blocking post-login feedback dialog. Mounted once in App.tsx
 * (inside AuthProvider and SnackbarProvider) so it survives the post-login navigation — the
 * trigger itself is fired from UserAuthForm via the lightweight triggerFeedbackCheck() context,
 * fire-and-forget, after navigate().
 */
export const FeedbackDialogProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
    const {showMessage} = useSnackbarEx();
    const {t} = useTranslation();
    const [feedbacks, setFeedbacks] = useState<OpenRequestFeedbackDTO[]>([]);

    const triggerFeedbackCheck = useCallback(async (roles: UserRoleType[] | undefined) => {
        if (!isClientRole(roles)) {
            // Defense-in-depth: the backend already returns 200 with an empty array [] for operator
            // roles, so this guard merely avoids an unnecessary request — it is not the only
            // enforcement point.
            return;
        }
        try {
            const response = await requestFeedbackApi.getAllOpen();
            const open = response.data;
            if (Array.isArray(open) && open.length > 0) {
                setFeedbacks(open);
            }
        } catch {
            // The feedback check is optional — errors must never block the login flow.
        }
    }, []);

    const handleConfirm = useCallback(
        async (entries: FeedbackEntryState[]) => {
            const answers = entries
                .filter((entry): entry is FeedbackEntryState & {reason: NonNullable<FeedbackEntryState["reason"]>} =>
                    entry.reason !== undefined
                )
                .map((entry) => ({
                    feedbackId: entry.feedbackId,
                    reason: entry.reason,
                    freeText: entry.freeText ?? undefined,
                    shareWithBidder: entry.shareWithBidder
                }));
            try {
                await requestFeedbackApi.answerAll({answers});
                setFeedbacks([]);
                showMessage({
                    severity: "success",
                    summary: t(PostLoginFeedbackDialogI18N.successMessage)
                });
            } catch {
                // A tolerant partial (already-answered markers skipped) still returns 200; any other
                // error must never block the app either — the markers stay PENDING and are simply
                // offered again on the next login. Both cases resolve identically: close silently.
                setFeedbacks([]);
            }
        },
        [showMessage, t]
    );

    const value = useMemo<FeedbackDialogContextType>(() => ({triggerFeedbackCheck}), [triggerFeedbackCheck]);

    return (
        <FeedbackDialogContext.Provider value={value}>
            {children}
            {feedbacks.length > 0 && (
                <PostLoginFeedbackDialog
                    // Stable key derived from the marker set: when the set of open feedbacks changes
                    // (e.g. a subsequent login yields different markers), React remounts the dialog so
                    // its internal entry-state is re-initialized fresh instead of retaining stale state.
                    key={feedbacks.map((fb) => fb.feedbackId).join(",")}
                    feedbacks={feedbacks}
                    onConfirm={handleConfirm}
                />
            )}
        </FeedbackDialogContext.Provider>
    );
};

export function useFeedbackDialog(): FeedbackDialogContextType {
    const context = useContext(FeedbackDialogContext);
    if (context === undefined) {
        throw new Error("useFeedbackDialog must be used within a FeedbackDialogProvider");
    }
    return context;
}
